import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Extract error message from FastAPI error response
export function getErrorMessage(error, defaultMessage = 'An error occurred') {
  const detail = error?.response?.data?.detail;
  if (!detail) return defaultMessage;
  if (Array.isArray(detail)) return detail.map(err => err.msg || JSON.stringify(err)).join(', ');
  if (typeof detail === 'object') return JSON.stringify(detail);
  return detail;
}

// ==================== Conversion constants ====================
// km→mi and km/h→mph share the same ratio (1 km = 0.621371 mi)
const METRIC_TO_IMPERIAL = 0.621371;
const IMPERIAL_TO_METRIC = 1 / METRIC_TO_IMPERIAL;

/** mm → inches (precipitation). 1 mm = 0.03937 in. */
export const MM_TO_INCHES = 0.03937;
export const HPA_TO_INHG = 0.0295299830714;
const MPS_TO_MPH = 2.2369362921;

// ==================== Null-safe coercion ====================
/** Coerce to number or return null for null/undefined/NaN. */
function safeNum(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

// ==================== Unit label helpers ====================

/** @param {'mph'|'kmh'} unit */
export function getSpeedLabel(unit) {
  return unit === 'mph' ? 'mph' : 'km/h';
}

/** @param {'mph'|'kmh'} unit */
export function getDistanceLabel(unit) {
  return unit === 'mph' ? 'mi' : 'km';
}

/** @param {'mph'|'kmh'} unit */
export function getTemperatureLabel(unit) {
  return unit === 'mph' ? '°F' : '°C';
}

export function getPressureLabel(unit) {
  return unit === 'mph' ? 'inHg' : 'hPa';
}

// ==================== Speed (backend stores km/h) ====================

/**
 * Convert speed from km/h to mph.
 * @param {number} kph
 * @returns {number|null}
 */
export function kphToMph(kph) {
  const n = safeNum(kph);
  return n === null ? null : n * METRIC_TO_IMPERIAL;
}

/**
 * Convert speed from mph to km/h.
 * @param {number} mph
 * @returns {number|null}
 */
export function mphToKph(mph) {
  const n = safeNum(mph);
  return n === null ? null : n * IMPERIAL_TO_METRIC;
}

/**
 * Convert speed from km/h to the user's display unit.
 * @param {number} kph - Speed in km/h (backend unit)
 * @param {'mph'|'kmh'} unit
 * @returns {number|null}
 */
export function speedToDisplay(kph, unit) {
  const n = safeNum(kph);
  if (n === null) return null;
  return unit === 'mph' ? n * METRIC_TO_IMPERIAL : n;
}

/**
 * Format speed for display (value + unit label).
 * @param {number} kph - Speed in km/h (backend unit)
 * @param {'mph'|'kmh'} unit
 * @param {number} [decimals=1]
 * @returns {string} e.g. "62.1 mph" or "100.0 km/h"
 */
export function formatSpeedByUnit(kph, unit, decimals = 1) {
  const val = speedToDisplay(kph, unit);
  if (val === null) return 'N/A';
  return `${val.toFixed(decimals)} ${getSpeedLabel(unit)}`;
}

/**
 * Convert a speed delta from km/h to the display unit.
 * @param {number} deltaKmh
 * @param {'mph'|'kmh'} unit
 * @returns {number}
 */
export function deltaToDisplay(deltaKmh, unit) {
  const n = safeNum(deltaKmh);
  if (n === null) return 0;
  return unit === 'mph' ? n * METRIC_TO_IMPERIAL : n;
}

/**
 * Format a speed delta for display with "faster" / "slower" suffix.
 * Returns null if the delta is effectively zero.
 * @param {number} deltaKmh - Delta in km/h
 * @param {'mph'|'kmh'} unit
 * @param {number} [decimals=1]
 * @returns {string|null} e.g. "1.2 mph slower" or "2.0 km/h faster"
 */
export function formatDeltaByUnit(deltaKmh, unit, decimals = 1) {
  const n = safeNum(deltaKmh);
  if (n === null) return null;
  const converted = deltaToDisplay(n, unit);
  const abs = Math.abs(converted);
  if (abs < 0.01) return null;
  const label = getSpeedLabel(unit);
  return n < 0
    ? `${abs.toFixed(decimals)} ${label} slower`
    : `${abs.toFixed(decimals)} ${label} faster`;
}

// ==================== Small distance — meters (braking points, etc.) ====================

const METERS_TO_FEET = 3.28084;

/**
 * Convert meters to display unit (meters or feet).
 * @param {number} m - Distance in meters
 * @param {'mph'|'kmh'} unit
 * @returns {number|null}
 */
export function metersToDisplay(m, unit) {
  const n = safeNum(m);
  if (n === null) return null;
  return unit === 'mph' ? n * METERS_TO_FEET : n;
}

/**
 * Label for small distances: "m" or "ft".
 * @param {'mph'|'kmh'} unit
 */
export function getSmallDistanceLabel(unit) {
  return unit === 'mph' ? 'ft' : 'm';
}

/** Decimal places for small distance display: 1 for ft (Imperial), 0 for m (metric). */
export function getSmallDistanceDecimals(unit) {
  return unit === 'mph' ? 1 : 0;
}

/**
 * Format a small distance (e.g. braking point) for display.
 * Imperial (ft): one decimal place (1 m ≈ 3.28 ft, so integer ft loses more precision).
 * Metric (m): whole number (integer precision is sufficient for brake markers).
 * @param {number} meters - Value in meters (backend unit)
 * @param {'mph'|'kmh'} unit
 * @returns {string} e.g. "49.2 ft" or "15 m"
 */
export function formatSmallDistance(meters, unit) {
  const val = metersToDisplay(meters, unit);
  if (val === null) return '–';
  return `${val.toFixed(getSmallDistanceDecimals(unit))} ${getSmallDistanceLabel(unit)}`;
}

// ==================== Distance — km (backend stores km) ====================

/**
 * Convert distance from km to the display unit.
 * @param {number} km
 * @param {'mph'|'kmh'} unit
 * @returns {number|null}
 */
export function distanceToDisplay(km, unit) {
  const n = safeNum(km);
  if (n === null) return null;
  return unit === 'mph' ? n * METRIC_TO_IMPERIAL : n;
}

/**
 * Format distance for display (value + unit label).
 * @param {number} km
 * @param {'mph'|'kmh'} unit
 * @param {number} [decimals=2]
 * @returns {string}
 */
export function formatDistanceByUnit(km, unit, decimals = 2) {
  const val = distanceToDisplay(km, unit);
  if (val === null) return 'N/A';
  return `${val.toFixed(decimals)} ${getDistanceLabel(unit)}`;
}

// ==================== Temperature (backend stores °C) ====================

/**
 * Convert Celsius to Fahrenheit.
 * @param {number} c
 * @returns {number|null}
 */
export function celsiusToFahrenheit(c) {
  const n = safeNum(c);
  return n === null ? null : n * 9 / 5 + 32;
}

/**
 * Convert Fahrenheit to Celsius.
 * @param {number} f
 * @returns {number|null}
 */
export function fahrenheitToCelsius(f) {
  const n = safeNum(f);
  return n === null ? null : (n - 32) * 5 / 9;
}

/**
 * Convert temperature from Celsius to the display unit.
 * @param {number} celsius
 * @param {'mph'|'kmh'} unit
 * @returns {number|null}
 */
export function temperatureToDisplay(celsius, unit) {
  const n = safeNum(celsius);
  if (n === null) return null;
  return unit === 'mph' ? celsiusToFahrenheit(n) : n;
}

/** Convert a source temperature to canonical Celsius. */
export function temperatureToCelsius(value, sourceUnit = '°C') {
  const n = safeNum(value);
  if (n === null) return null;
  return /f/i.test(String(sourceUnit)) ? fahrenheitToCelsius(n) : n;
}

/** Convert a source wind speed to canonical metres per second. */
export function windSpeedToMps(value, sourceUnit = 'm/s') {
  const n = safeNum(value);
  if (n === null) return null;
  const normalized = String(sourceUnit).trim().toLowerCase();
  if (normalized === 'mph') return n / MPS_TO_MPH;
  if (['km/h', 'kmh', 'kph'].includes(normalized)) return n / 3.6;
  if (['kn', 'kt', 'knot', 'knots'].includes(normalized)) return n * 0.514444;
  return n;
}

export function formatWindSpeedByUnit(mps, unit, decimals = 1) {
  const n = safeNum(mps);
  if (n === null) return 'N/A';
  const value = unit === 'mph' ? n * MPS_TO_MPH : n * 3.6;
  return `${value.toFixed(decimals)} ${getSpeedLabel(unit)}`;
}

/** Convert a source pressure to canonical hPa. */
export function pressureToHpa(value, sourceUnit = 'hPa') {
  const n = safeNum(value);
  if (n === null) return null;
  const normalized = String(sourceUnit).trim().toLowerCase();
  let hpa = n;
  if (normalized === 'inhg' || normalized.includes('inch')) hpa = n / HPA_TO_INHG;
  else if (normalized === 'kpa') hpa = n * 10;
  else if (normalized === 'pa') hpa = n / 100;
  else if (normalized === 'mmhg') hpa = n * 1.3332239;

  // Reject values that cannot be atmospheric pressure. Some Alkamel feeds
  // declare inHg but populate this field with humidity-like percentages.
  return hpa >= 250 && hpa <= 1100 ? hpa : null;
}

export function formatPressureByUnit(hpa, unit) {
  const n = safeNum(hpa);
  if (n === null) return 'N/A';
  const value = unit === 'mph' ? n * HPA_TO_INHG : n;
  return `${value.toFixed(unit === 'mph' ? 2 : 0)} ${getPressureLabel(unit)}`;
}

export function formatPrecipitationByUnit(mm, unit) {
  const n = safeNum(mm);
  if (n === null) return 'N/A';
  return unit === 'mph' ? `${(n * MM_TO_INCHES).toFixed(2)} in` : `${n.toFixed(1)} mm`;
}

export function formatVisibilityByUnit(km, unit) {
  const n = safeNum(km);
  if (n === null) return 'N/A';
  const value = unit === 'mph' ? n * METRIC_TO_IMPERIAL : n;
  return `${value.toFixed(1)} ${getDistanceLabel(unit)}`;
}

// ==================== Structured tip renderer ====================
// Backend returns tips as either:
//   - plain string  → displayed as-is (fallback)
//   - { text: "...", values: { key: { kind, kmh } } }  → tokens substituted with converted values
//
// This renderer handles both, plus garbage / malformed output gracefully.

// Regexes for measurement literals in plain-string tips (speed, distance, temperature)
const SPEED_IN_TIP_PATTERN = /(\d+(?:\.\d+)?)\s*(km\/h|kph|kmh|mph)/gi;
const DISTANCE_SMALL_PATTERN = /(\d+(?:\.\d+)?)\s*(m|ft|feet|meters?)\b/gi;
const DISTANCE_LARGE_PATTERN = /(\d+(?:\.\d+)?)\s*(km|mi|miles?)\b/gi;
// Temperature: require ° for symbol form (°C/°F), or at least one space before bare C/F plus not followed by letter (avoids "CF", "FC", "5C")
const TEMP_PATTERN = /([-+]?\d+(?:\.\d+)?)(?:\s*°\s*(C|F)|\s+(C|F)(?![a-zA-Z]))/gi;

/**
 * Convert all measurement literals in a plain string to the target unit (Imperial/Metric).
 * Handles: speed (km/h, mph), small distance (m, ft), large distance (km, mi), temperature (°C, °F).
 * Use for quick tips, AI coach response text, or any copy that may contain measurements.
 *
 * The regex patterns (speed, distance, temperature) are designed to match disjoint spans:
 * speed uses unit suffixes like km/h/mph; distance uses m/ft/km/mi with word boundaries;
 * temperature uses °C/°F or number + space + C/F (not followed by letter). So e.g. "5 km/h wind at 20°C" or "32 F"
 * has non-overlapping matches, and sequential replace() calls do not shift indices in a way that
 * breaks later matches. Future changes should keep patterns non-overlapping.
 *
 * @param {string} text - Tip or message text that may contain measurements
 * @param {'mph'|'kmh'} targetUnit - 'mph' = Imperial, 'kmh' = Metric
 * @returns {string}
 */
export function convertPlainTipUnits(text, targetUnit) {
  if (!text || typeof text !== 'string') return text;
  let out = text;

  out = out.replace(SPEED_IN_TIP_PATTERN, (match, numStr, unitStr) => {
    const num = safeNum(parseFloat(numStr));
    if (num === null) return match;
    const isMph = /mph/i.test(unitStr);
    const kmh = isMph ? mphToKph(num) : num;
    if (kmh === null) return match;
    const displayVal = speedToDisplay(kmh, targetUnit);
    const label = getSpeedLabel(targetUnit);
    return `${(displayVal !== null ? displayVal : kmh).toFixed(1)} ${label}`;
  });

  out = out.replace(DISTANCE_SMALL_PATTERN, (match, numStr, unitStr) => {
    const num = safeNum(parseFloat(numStr));
    if (num === null) return match;
    const isFt = /ft|feet/i.test(unitStr);
    const meters = isFt ? num / METERS_TO_FEET : num;
    const displayVal = metersToDisplay(meters, targetUnit);
    const label = getSmallDistanceLabel(targetUnit);
    return `${(displayVal !== null ? displayVal : meters).toFixed(getSmallDistanceDecimals(targetUnit))} ${label}`;
  });

  out = out.replace(DISTANCE_LARGE_PATTERN, (match, numStr, unitStr) => {
    const num = safeNum(parseFloat(numStr));
    if (num === null) return match;
    const isMi = /mi|miles?/i.test(unitStr);
    const km = isMi ? num / METRIC_TO_IMPERIAL : num;
    const displayVal = distanceToDisplay(km, targetUnit);
    const label = getDistanceLabel(targetUnit);
    return `${(displayVal !== null ? displayVal : km).toFixed(2)} ${label}`;
  });

  out = out.replace(TEMP_PATTERN, (match, numStr, unitFromDegree, unitFromBare) => {
    const num = safeNum(parseFloat(numStr));
    if (num === null) return match;
    const unitStr = unitFromDegree ?? unitFromBare;
    if (!unitStr) return match;
    const isF = /F/i.test(unitStr);
    const celsius = isF ? fahrenheitToCelsius(num) : num;
    const displayVal = temperatureToDisplay(celsius, targetUnit);
    const label = getTemperatureLabel(targetUnit);
    return `${(displayVal !== null ? displayVal : celsius).toFixed(0)}${label}`;
  });

  return out;
}

/**
 * Render a single tip (structured or plain) into a display string.
 * Handles: plain strings, { text, values } structured tips, and stringified JSON.
 * @param {string|object} tip - A plain string or { text, values } structured tip
 * @param {'mph'|'kmh'} unit - The user's display unit (defaults to 'kmh' if invalid)
 * @returns {string} Human-readable tip text
 */
export function renderTip(tip, unit) {
  const speedUnit = unit === 'mph' || unit === 'kmh' ? unit : 'kmh';

  // Plain string — convert any embedded measurements (speed, distance, temp) to target unit
  if (typeof tip === 'string') {
    if (tip.trim().startsWith('{') && tip.trim().endsWith('}')) {
      try {
        const parsed = JSON.parse(tip);
        if (parsed && typeof parsed === 'object') tip = parsed;
      } catch (_) {
        return convertPlainTipUnits(tip, speedUnit);
      }
    } else {
      return convertPlainTipUnits(tip, speedUnit);
    }
  }

  // Null / unexpected type — safe fallback
  if (!tip || typeof tip !== 'object') return '';

  const text = tip.text ?? tip.content;
  const values = tip.values ?? tip.value;

  // Missing or non-string text — safe fallback (no leaking internal structure)
  if (typeof text !== 'string') return '';

  // No values dict — still convert any measurements in the text (e.g. backend sent plain string as object)
  if (!values || typeof values !== 'object') {
    return convertPlainTipUnits(text, speedUnit);
  }

  // Replace {key} tokens with converted values (speed, speed_delta, distance, temperature)
  let result = text.replace(/\{(\w+)\}/g, (match, key) => {
    const val = values[key];
    if (!val || typeof val !== 'object') return match; // leave token as-is

    const kind = val.kind;
    const n = safeNum(val.kmh ?? val.km ?? val.meters ?? val.m ?? val.celsius ?? val.c);
    if (n === null) return match;

    if (kind === 'speed') {
      const converted = speedToDisplay(n, speedUnit);
      return converted !== null
        ? `${converted.toFixed(1)} ${getSpeedLabel(speedUnit)}`
        : match;
    }

    if (kind === 'speed_delta') {
      const abs = Math.abs(deltaToDisplay(n, speedUnit));
      return `${abs.toFixed(1)} ${getSpeedLabel(speedUnit)}`;
    }

    if (kind === 'distance' || kind === 'distance_small') {
      const converted = metersToDisplay(n, speedUnit);
      const label = getSmallDistanceLabel(speedUnit);
      return converted !== null ? `${converted.toFixed(getSmallDistanceDecimals(speedUnit))} ${label}` : match;
    }

    if (kind === 'distance_km') {
      const converted = distanceToDisplay(n, speedUnit);
      const label = getDistanceLabel(speedUnit);
      return converted !== null ? `${converted.toFixed(2)} ${label}` : match;
    }

    if (kind === 'temperature') {
      const converted = temperatureToDisplay(n, speedUnit);
      const label = getTemperatureLabel(speedUnit);
      return converted !== null ? `${converted.toFixed(0)}${label}` : match;
    }

    return match; // unknown kind — leave token
  });

  // Only convert literals if a token was missing; otherwise backend sends speed + distance as tokens
  if (/\{\w+\}/.test(result)) {
    result = convertPlainTipUnits(result, speedUnit);
  }
  return result;
}

/**
 * Render an array of tips (mixed structured + plain strings).
 * Filters out empty results and limits to maxTips.
 * @param {Array<string|object>} tips
 * @param {'mph'|'kmh'} unit
 * @param {number} [maxTips=3]
 * @returns {string[]}
 */
export function renderTips(tips, unit, maxTips = 3) {
  if (!Array.isArray(tips)) return [];
  const speedUnit = unit === 'mph' || unit === 'kmh' ? unit : 'kmh';
  return tips
    .map(t => renderTip(t, speedUnit))
    .filter(t => t && t.length > 0)
    .slice(0, maxTips);
}
